import { useForm } from 'react-hook-form'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Content,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Button,
  Indicators,
} from 'ui'
import { tryCatch } from 'utils'
import { FormErrors } from '../../../Forms/types'
import toast from 'react-hot-toast'

const forgotSchema = Yup.object().shape({
  email: Yup.string().required().email(),
})
export type Forgot = Yup.InferType<typeof forgotSchema>

/**
 * A simple forgot password form for existing users.
 */
export const ForgotPassword: React.FC = () => {
  const [complete, setComplete] = useState(false)

  const form = useForm<Forgot>({
    mode: 'onBlur',
    resolver: yupResolver(forgotSchema as any) as any,
  })

  const handleFromSubmission = async (data: Forgot) => {
    await tryCatch(
      async () => {
        try {
          const response = await fetch(
            `${
              import.meta.env.VITE_EPC_API_URL ??
              import.meta.env.REACT_APP_API_URL ??
              ''
            }/auth/forgot`,
            {
              body: JSON.stringify({ email: data.email as string }),
              method: 'POST',
            }
          )
          const { errors } = await response.json()
          if (errors && Object.keys(errors).length > 0) {
            if (errors.user) {
              toast.error('User with the specified email does not exist.')
            } else {
              errors?.length
                ? (errors as FormErrors).forEach(({ path, message }) =>
                    form.setError(path as any, { message })
                  )
                : form.reset({})
            }
          } else {
            setComplete(true)
          }
        } catch (e) {
          toast.error('Could not reach server.')
        }
      },
      (error: Error) => {
        toast.error(error.message)
      }
    )
  }

  return complete ? (
    <Alert.Dismissable>
      <Content.Body marginClassNames="m-0">
        Please check your email.
      </Content.Body>
    </Alert.Dismissable>
  ) : (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFromSubmission)}
        className="mt-8 flex flex-col"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input placeholder="my@emailaddress.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          variant="default"
          className="ml-auto mt-4"
        >
          {form.formState.isSubmitting ? (
            <Indicators.Spinner
              size="lg"
              className="mt-auto mr-2 mb-auto ml-auto"
            />
          ) : null}
          <span>Send Reset Email</span>
        </Button>
      </form>
    </Form>
  )
}
